import React, { useState, useEffect } from 'react';
import { Puzzle, CheckCircle, AlertTriangle, Clock, Shield, Waves, Cloud, MapPin, Brain, XCircle, Sparkles } from 'lucide-react';
import PageShell from '../components/PageShell';
import { motion } from 'framer-motion';
import { ProBadge } from '../components/ProGate';
import { useSubscription } from '../contexts/SubscriptionContext';
import { authFetch, API } from '../utils/api';


const CATEGORY_ORDER = ['Calendars', 'Fitness', 'Lifestyle'];

const STATUS_STYLES = {
  active: { label: 'Active', className: 'bg-green-500/20 text-green-400', Icon: CheckCircle },
  maintenance: { label: 'Maintenance', className: 'bg-yellow-500/20 text-yellow-400', Icon: AlertTriangle },
  not_configured: { label: 'No Key', className: 'bg-red-500/15 text-red-400/80', Icon: XCircle },
  planned: { label: 'Planned', className: 'bg-white/10 text-muted-foreground', Icon: Clock },
};

const CATEGORY_ICONS = {
  ai: Brain,
  weather: Cloud,
  surf: Waves,
  utility: MapPin,
  auth: Shield,
  other: Puzzle,
};

const CATEGORY_LABELS = {
  ai: 'AI & Intelligence',
  weather: 'Weather & Ocean',
  surf: 'Surf Data',
  utility: 'Utilities',
  auth: 'Authentication',
};

function PlatformIntegrationCard({ integration, index }) {
  const style = STATUS_STYLES[integration.status] || STATUS_STYLES.planned;
  const { Icon } = style;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="flex items-center gap-3 py-3 border-b border-white/[0.04] last:border-b-0"
      data-testid={`platform-integration-${integration.id}`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
        integration.status === 'active' ? 'bg-green-500/10 border border-green-500/20' :
        integration.status === 'maintenance' ? 'bg-yellow-500/10 border border-yellow-500/20' :
        integration.status === 'not_configured' ? 'bg-red-500/5 border border-red-500/10' :
        'bg-bg-subtle border border-white/10'
      }`}>
        <Icon className={`w-4 h-4 ${
          integration.status === 'active' ? 'text-green-400' :
          integration.status === 'maintenance' ? 'text-yellow-400' :
          integration.status === 'not_configured' ? 'text-red-400/60' :
          'text-muted-foreground'
        }`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{integration.name}</p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{integration.description}</p>
      </div>
      <span className={`text-[10px] px-2 py-1 rounded-lg font-medium flex-shrink-0 ${style.className}`}>
        {style.label}
      </span>
    </motion.div>
  );
}

export default function Integrations() {
  const { isPro } = useSubscription();
  const [integrations, setIntegrations] = useState([]);
  const [platformIntegrations, setPlatformIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [intRes, platRes] = await Promise.all([
          authFetch(`${API}/api/integrations`),
          authFetch(`${API}/api/platform-integrations`),
        ]);
        if (intRes.ok) setIntegrations((await intRes.json()).integrations || []);
        if (platRes.ok) setPlatformIntegrations((await platRes.json()).platform_integrations || []);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) {
    return (
      <PageShell title="Integration Library">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          </div>
        </div>
      </PageShell>
    );
  }

  // Group custom integrations
  const grouped = {};
  integrations.forEach(i => {
    const cat = i.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(i);
  });

  // Group platform integrations by category
  const platformGrouped = {};
  platformIntegrations.forEach(pi => {
    const cat = pi.category || 'other';
    if (!platformGrouped[cat]) platformGrouped[cat] = [];
    platformGrouped[cat].push(pi);
  });

  const activeCount = platformIntegrations.filter(p => p.status === 'active').length;
  const maintenanceCount = platformIntegrations.filter(p => p.status === 'maintenance').length;
  const totalCount = platformIntegrations.length;

  // Ordered categories for platform integrations
  const platformCatOrder = ['ai', 'auth', 'weather', 'surf', 'utility', 'other'];

  return (
    <PageShell title="Integration Library">
      <div className="p-5 space-y-5" data-testid="integrations-page">
        {/* Pro upgrade banner for free users */}
        {!isPro && (
          <div className="bg-primary/5 border border-primary/15 rounded-2xl p-3 flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
            <p className="text-[11px] text-muted-foreground flex-1">Upgrade to <span className="text-primary font-semibold">Swell Pro</span> to connect integrations</p>
            <ProBadge />
          </div>
        )}
        {/* Custom Integrations Card */}
        <div className="bg-bg-subtle/80 border border-white/10 rounded-2xl p-5 flex items-center justify-between">
          <div>
            <p className="font-medium text-sm">Custom Integrations</p>
            <p className="text-xs text-muted-foreground mt-0.5">Connect Swell to other apps</p>
          </div>
          <span className="bg-white/10 text-xs text-muted-foreground font-medium px-3 py-1.5 rounded-xl">
            Soon
          </span>
        </div>

        {/* Custom Integration Categories */}
        {CATEGORY_ORDER.map(cat => {
          const items = grouped[cat];
          if (!items) return null;
          return (
            <div key={cat}>
              <p className="font-heading text-sm font-semibold mb-3">{cat}</p>
              <div className="space-y-2">
                {items.map(item => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 py-3 border-b border-white/5 last:border-b-0"
                    data-testid={`integration-${item.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-bg-subtle border border-white/10 flex items-center justify-center flex-shrink-0">
                      <Puzzle className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>
                    </div>
                    {item.status === 'connected' ? (
                      <span className="text-[10px] text-green-400 bg-green-500/20 px-2 py-1 rounded-lg font-medium flex-shrink-0">
                        Connected
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/60 bg-white/5 px-2 py-1 rounded-lg flex-shrink-0">
                        Coming soon
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Platform Integrations Section */}
        <div data-testid="platform-integrations-section">
          <div className="flex items-center justify-between mb-2">
            <p className="font-heading text-sm font-semibold">Platform Integrations</p>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <span className="text-[9px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded-md" data-testid="active-count">
                  {activeCount} active
                </span>
              )}
              {maintenanceCount > 0 && (
                <span className="text-[9px] text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded-md">
                  {maintenanceCount} maintenance
                </span>
              )}
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mb-4">
            {totalCount} data sources powering Swell. Status auto-updates based on availability.
          </p>

          {/* Grouped by category */}
          {platformCatOrder.map(cat => {
            const items = platformGrouped[cat];
            if (!items || items.length === 0) return null;
            const CatIcon = CATEGORY_ICONS[cat] || Puzzle;
            const catLabel = CATEGORY_LABELS[cat] || cat;
            return (
              <div key={cat} className="mb-4" data-testid={`platform-category-${cat}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <CatIcon className="w-3 h-3 text-muted-foreground/40" />
                  <p className="text-[10px] text-muted-foreground/50 uppercase tracking-widest font-bold">{catLabel}</p>
                </div>
                <div className="bg-bg-subtle/50 border border-white/5 rounded-2xl overflow-hidden px-4">
                  {items.map((pi, idx) => (
                    <PlatformIntegrationCard key={pi.id} integration={pi} index={idx} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PageShell>
  );
}
